import React from 'react';
import type { ProgramWorkout } from '../types';

interface ExerciseSelectionModalProps {
  workout: ProgramWorkout;
  exerciseProgress: Array<{
    exercise_id: string;
    sets: any[];
    notes: string;
    isCompleted: boolean;
  }>;
  onSelectExercise: (index: number) => void;
  onFinishWorkout: () => void;
}

/**
 * ExerciseSelectionModal - Modal for selecting next exercise during workout
 */
export const ExerciseSelectionModal: React.FC<ExerciseSelectionModalProps> = ({
  workout,
  exerciseProgress,
  onSelectExercise,
  onFinishWorkout
}) => {
  const allCompleted = exerciseProgress.every(p => p.isCompleted);
  const completedCount = exerciseProgress.filter(p => p.isCompleted).length;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="mb-6">
            <h2 className="text-2xl font-bold mb-2">
              {allCompleted ? 'Все упражнения выполнены!' : 'Выберите следующее упражнение'}
            </h2>
            <p className="text-gray-600">
              Выполнено: {completedCount} из {workout.exercises.length}
            </p>
          </div>

          {/* Exercise List */}
          <div className="space-y-3 mb-6">
            {workout.exercises.map((exercise, index) => {
              const progress = exerciseProgress[index];
              const isCompleted = progress.isCompleted;
              const setsCount = progress.sets.length;
              const targetSets = exercise.target_sets || 0;
              const progressPercent = targetSets > 0 ? (setsCount / targetSets) * 100 : 0;

              return (
                <button
                  key={exercise.id}
                  onClick={() => onSelectExercise(index)}
                  disabled={isCompleted}
                  className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                    isCompleted
                      ? 'bg-green-50 border-green-300 opacity-60 cursor-not-allowed'
                      : 'bg-white border-gray-200 hover:border-blue-500 hover:shadow-md'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        {isCompleted && (
                          <span className="text-green-600">✓</span>
                        )}
                        <h3 className="font-medium">
                          {index + 1}. {exercise.exercise.name_ru}
                        </h3>
                      </div>
                      <p className="text-sm text-gray-600">
                        {exercise.target_sets && `${exercise.target_sets} подходов`}
                        {(exercise.target_reps_min || exercise.target_reps_max) && 
                          ` × ${exercise.target_reps_min || '?'}-${exercise.target_reps_max || '?'} повторов`
                        }
                      </p>
                    </div>
                    {!isCompleted && (
                      <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    )}
                  </div>

                  {/* Progress bar */}
                  {targetSets > 0 && (
                    <div className="mt-2">
                      <div className="flex justify-between text-xs text-gray-500 mb-1">
                        <span>{setsCount} / {targetSets} подходов</span>
                        <span>{Math.round(progressPercent)}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full transition-all ${
                            isCompleted ? 'bg-green-500' : 'bg-blue-500'
                          }`}
                          style={{ width: `${Math.min(progressPercent, 100)}%` }}
                        />
                      </div>
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Action Button */}
          {allCompleted ? (
            <button
              onClick={onFinishWorkout}
              className="w-full bg-green-600 text-white py-4 px-6 rounded-lg font-medium text-lg hover:bg-green-700"
            >
              Завершить тренировку
            </button>
          ) : (
            <button
              onClick={onFinishWorkout}
              className="w-full bg-gray-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-gray-700"
            >
              Завершить тренировку досрочно
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExerciseSelectionModal;
